import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import { FinancialCard, StatusCard, PeopleCard, NotesCard, ActivityLogCard } from "../components/TransactionSections";
import { DocumentsPanel } from "../components/DocumentsPanel";
import { WorkflowPanel } from "../components/WorkflowPanel";
import EmailPreviewDialog from "../components/EmailPreviewDialog";
import { ZillowLinkCard } from "../components/transactions/ZillowLinkCard";
import { TransactionHeader } from "../components/transactions/TransactionHeader";
import { KeyDatesPanel } from "../components/transactions/KeyDatesPanel";
import { TasksPanel } from "../components/transactions/TasksPanel";
import { NotifyPartiesDialog } from "../components/transactions/NotifyPartiesDialog";

const TransactionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const [transaction, setTransaction] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingAddress, setEditingAddress] = useState(false);
  const [addressValue, setAddressValue] = useState("");
  const [expandedGroups, setExpandedGroups] = useState({});
  const [showAddTask, setShowAddTask] = useState(false);
  const [newTask, setNewTask] = useState({ title: '', due_date: '', assigned_to_type: '', description: '', task_type: 'task', email_subject: '', email_body: '', email_recipients: [], email_cc: '', email_bcc: '' });
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editTaskData, setEditTaskData] = useState({});
  const [activeUsers, setActiveUsers] = useState([]);
  const [emailPreview, setEmailPreview] = useState(null);
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showNotifyDialog, setShowNotifyDialog] = useState(false);
  const [notifyData, setNotifyData] = useState({ recipients: [], cc: [], bcc: [], subject: "", body: "" });
  const [sendingNotify, setSendingNotify] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [txnRes, contactRes, usersRes] = await Promise.all([
        axios.get(`${API}/transactions/${id}`, { withCredentials: true }),
        axios.get(`${API}/contacts`, { withCredentials: true }),
        axios.get(`${API}/users`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      setTransaction(txnRes.data);
      setContacts(contactRes.data);
      setActiveUsers(Array.isArray(usersRes.data) ? usersRes.data.filter(u => u.is_active !== false) : []);
      setEditData({
        contract_date: txnRes.data.contract_date?.split('T')[0] || '',
        closing_date: txnRes.data.closing_date?.split('T')[0] || '',
        earnest_money: txnRes.data.earnest_money || '',
        earnest_money_deadline: txnRes.data.earnest_money_deadline?.split('T')[0] || '',
        inspection_deadline: txnRes.data.inspection_deadline?.split('T')[0] || '',
        financing_deadline: txnRes.data.financing_deadline?.split('T')[0] || '',
        appraisal_deadline: txnRes.data.appraisal_deadline?.split('T')[0] || '',
        insurance_deadline: txnRes.data.insurance_deadline?.split('T')[0] || '',
        possession_date: txnRes.data.possession_date?.split('T')[0] || '',
        purchase_price: txnRes.data.purchase_price || '',
        status: txnRes.data.status,
        notes: txnRes.data.notes || ''
      });

      const groups = {};
      txnRes.data.tasks?.forEach(task => {
        if (task.group_name) {
          groups[task.group_name] = true;
        }
      });
      setExpandedGroups(groups);
    } catch (error) {
      toast.error("Failed to load transaction");
      navigate("/transactions");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getAssigneeName = (assignedToType) => {
    if (!assignedToType) return null;
    if (assignedToType.startsWith("coordinator:")) {
      const uid = assignedToType.replace("coordinator:", "");
      const u = activeUsers.find((u) => u.user_id === uid);
      return u ? u.name : "Coordinator";
    }
    return assignedToType.replace(/_/g, " ");
  };

  const handleSaveAddress = async () => {
    if (!addressValue.trim()) return;
    try {
      await axios.put(`${API}/transactions/${id}`, { property_address: addressValue.trim() }, { withCredentials: true });
      toast.success("Address updated");
      setEditingAddress(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update address");
    }
  };

  const handleUpdateTransaction = async () => {
    try {
      const payload = {
        ...editData,
        contract_date: editData.contract_date ? editData.contract_date + "T12:00:00" : null,
        closing_date: editData.closing_date ? editData.closing_date + "T12:00:00" : null,
        earnest_money: editData.earnest_money ? parseFloat(editData.earnest_money) : null,
        earnest_money_deadline: editData.earnest_money_deadline ? editData.earnest_money_deadline + "T12:00:00" : null,
        inspection_deadline: editData.inspection_deadline ? editData.inspection_deadline + "T12:00:00" : null,
        financing_deadline: editData.financing_deadline ? editData.financing_deadline + "T12:00:00" : null,
        appraisal_deadline: editData.appraisal_deadline ? editData.appraisal_deadline + "T12:00:00" : null,
        insurance_deadline: editData.insurance_deadline ? editData.insurance_deadline + "T12:00:00" : null,
        possession_date: editData.possession_date ? editData.possession_date + "T12:00:00" : null,
        purchase_price: editData.purchase_price ? parseFloat(editData.purchase_price) : null,
      };
      await axios.put(`${API}/transactions/${id}`, payload, { withCredentials: true });
      toast.success("Transaction updated. Deadlines recalculated.");
      setIsEditing(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update transaction");
    }
  };

  const handleDeleteTransaction = async () => {
    try {
      await axios.delete(`${API}/transactions/${id}`, { withCredentials: true });
      toast.success("Transaction deleted");
      navigate("/transactions");
    } catch (error) {
      toast.error("Failed to delete transaction");
    }
  };

  const handleTaskStatusChange = async (taskId, newStatus) => {
    try {
      await axios.put(`${API}/transactions/${id}/tasks/${taskId}`, { status: newStatus }, { withCredentials: true });
      toast.success("Task updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update task");
    }
  };

  const handleSendReminder = async (taskId) => {
    try {
      await axios.post(`${API}/send-reminder?transaction_id=${id}&task_id=${taskId}`, {}, { withCredentials: true });
      toast.success("Reminder sent");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reminder");
    }
  };

  const handleEmailPreview = async (taskId) => {
    try {
      const res = await axios.get(`${API}/transactions/${id}/tasks/${taskId}/email-preview`, { withCredentials: true });
      setEmailPreview({ ...res.data, task_id: taskId });
      setShowEmailPreview(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load email preview");
    }
  };

  const handleSendEmail = async () => {
    if (!emailPreview?.task_id) return;
    setSendingEmail(true);
    try {
      const res = await axios.post(`${API}/transactions/${id}/tasks/${emailPreview.task_id}/send-email`, {
        recipients: emailPreview.recipients || [],
        cc: emailPreview.cc || [],
        bcc: emailPreview.bcc || [],
        subject: emailPreview.subject || "",
        body: emailPreview.body || "",
      }, { withCredentials: true });
      toast.success(res.data.message);
      setShowEmailPreview(false);
      setEmailPreview(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleNotifyParties = async () => {
    try {
      const res = await axios.get(`${API}/transactions/${id}/notify-deadline-preview`, { withCredentials: true });
      setNotifyData({
        recipients: res.data.suggested_recipients || [],
        cc: [],
        bcc: [],
        subject: res.data.subject || "",
        body: res.data.body || "",
      });
      setShowNotifyDialog(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load notification preview");
    }
  };

  const handleSendNotify = async () => {
    if (!notifyData.recipients.length) {
      toast.error("Add at least one recipient");
      return;
    }
    let body = notifyData.body;
    if (notifyData.editingBody && notifyData.bodyText) {
      const html = notifyData.bodyText
        .split("\n")
        .map(line => line.trim() ? `<p style="margin:0 0 6px;font-size:14px;color:#1a1a1a;">${line}</p>` : "<br/>")
        .join("");
      body = `<div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;line-height:1.6;">${html}<div style="margin-top:24px;padding-top:16px;border-top:1px solid #e2e8f0;"><p style="color:#64748b;font-size:12px;margin:0;">Sent from EasyOffice Transaction Manager</p></div></div>`;
    }
    setSendingNotify(true);
    try {
      const res = await axios.post(`${API}/transactions/${id}/notify-deadline-changes`, {
        recipient_emails: notifyData.recipients,
        cc: notifyData.cc,
        bcc: notifyData.bcc,
        subject: notifyData.subject,
        body,
      }, { withCredentials: true });
      toast.success(`Notification sent to ${res.data.sent} recipient(s)`);
      setShowNotifyDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send notification");
    } finally {
      setSendingNotify(false);
    }
  };

  const handleAddTask = async () => {
    if (!newTask.title || !newTask.due_date) return;
    try {
      await axios.post(`${API}/transactions/${id}/tasks`, newTask, { withCredentials: true });
      setNewTask({ title: '', due_date: '', assigned_to_type: '', description: '', task_type: 'task', email_subject: '', email_body: '', email_recipients: [], email_cc: '', email_bcc: '' });
      setShowAddTask(false);
      fetchData();
    } catch (err) {
      toast.error("Failed to add task");
    }
  };

  const startEditTask = (task) => {
    if (task === null) { setEditingTaskId(null); return; }
    setEditingTaskId(task.task_id);
    setEditTaskData({
      title: task.title,
      due_date: String(task.due_date || '').split('T')[0] || '',
      assigned_to_type: task.assigned_to_type || '',
      description: task.description || '',
      status: task.status
    });
  };

  const handleSaveEditTask = async (taskId) => {
    try {
      await axios.put(`${API}/transactions/${id}/tasks/${taskId}`, {
        status: editTaskData.status,
        title: editTaskData.title,
        due_date: editTaskData.due_date,
        assigned_to_type: editTaskData.assigned_to_type,
        description: editTaskData.description
      }, { withCredentials: true });
      setEditingTaskId(null);
      fetchData();
    } catch (err) {
      toast.error("Failed to update task");
    }
  };

  const isTaskOverdue = (task) => {
    if (task.status === 'completed') return false;
    try {
      const s = String(task.due_date || '');
      const datePart = s.includes('T') ? s.split('T')[0] : s.split(' ')[0];
      if (!datePart) return false;
      const [y, m, d] = datePart.split('-').map(Number);
      if (!y || !m || !d) return false;
      const dueDay = new Date(y, m - 1, d);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return dueDay < today;
    } catch { return false; }
  };

  const groupedTasks = () => {
    const groups = {};
    transaction?.tasks?.forEach(task => {
      const groupName = task.group_name || 'Other Tasks';
      if (!groups[groupName]) groups[groupName] = [];
      groups[groupName].push(task);
    });
    return groups;
  };

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="h-64 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!transaction) return null;

  const taskGroups = groupedTasks();
  const completedTasks = transaction.tasks?.filter(t => t.status === 'completed').length || 0;
  const totalTasks = transaction.tasks?.length || 0;

  return (
    <div className="p-6 md:p-8 animate-fade-in">
      <TransactionHeader
        transaction={transaction}
        isAdmin={isAdmin}
        isEditing={isEditing}
        setIsEditing={setIsEditing}
        editingAddress={editingAddress}
        setEditingAddress={setEditingAddress}
        addressValue={addressValue}
        setAddressValue={setAddressValue}
        onSaveAddress={handleSaveAddress}
        onUpdate={handleUpdateTransaction}
        onNotifyParties={handleNotifyParties}
        onShowDelete={() => setShowDeleteDialog(true)}
        onBack={() => navigate("/transactions")}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          <KeyDatesPanel
            transaction={transaction}
            isEditing={isEditing}
            editData={editData}
            setEditData={setEditData}
          />

          <TasksPanel
            transaction={transaction}
            taskGroups={taskGroups}
            completedTasks={completedTasks}
            totalTasks={totalTasks}
            expandedGroups={expandedGroups}
            setExpandedGroups={setExpandedGroups}
            showAddTask={showAddTask}
            setShowAddTask={setShowAddTask}
            newTask={newTask}
            setNewTask={setNewTask}
            onAddTask={handleAddTask}
            editingTaskId={editingTaskId}
            editTaskData={editTaskData}
            setEditTaskData={setEditTaskData}
            onStartEditTask={startEditTask}
            onSaveEditTask={handleSaveEditTask}
            onTaskStatusChange={handleTaskStatusChange}
            onEmailPreview={handleEmailPreview}
            onSendReminder={handleSendReminder}
            isTaskOverdue={isTaskOverdue}
            getAssigneeName={getAssigneeName}
            activeUsers={activeUsers}
            currentUserId={user?.user_id}
            contacts={contacts}
          />
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <FinancialCard transaction={transaction} isEditing={isEditing} editData={editData} setEditData={setEditData} />
          <StatusCard transaction={transaction} isEditing={isEditing} editData={editData} setEditData={setEditData} />
          <PeopleCard transaction={transaction} isEditing={isEditing} activeUsers={activeUsers} contacts={contacts} onSave={async (data) => {
            try {
              await axios.put(`${API}/transactions/${id}`, data, { withCredentials: true });
              fetchData();
            } catch { toast.error("Failed to save people"); }
          }} />
          <NotesCard transaction={transaction} onSave={async (notes) => {
            try {
              await axios.put(`${API}/transactions/${id}`, { notes }, { withCredentials: true });
              fetchData();
            } catch { toast.error("Failed to save notes"); }
          }} />
          <DocumentsPanel transactionId={id} />
          <ZillowLinkCard transaction={transaction} onUpdate={fetchData} onSave={async (url) => {
            try {
              await axios.put(`${API}/transactions/${id}`, { zillow_url: url }, { withCredentials: true });
              fetchData();
            } catch { toast.error("Failed to save Zillow link"); }
          }} />
          <WorkflowPanel transaction={transaction} onUpdate={(updated) => setTransaction(updated)} />
          <ActivityLogCard transaction={transaction} />
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent data-testid="delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete Transaction</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this transaction? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteTransaction}
              data-testid="confirm-delete-btn"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Preview & Send Dialog */}
      <EmailPreviewDialog
        open={showEmailPreview}
        onClose={() => { setShowEmailPreview(false); setEmailPreview(null); }}
        emailPreview={emailPreview}
        setEmailPreview={setEmailPreview}
        onSend={handleSendEmail}
        sending={sendingEmail}
      />

      {/* Notify Parties Dialog */}
      <NotifyPartiesDialog
        open={showNotifyDialog}
        onOpenChange={setShowNotifyDialog}
        notifyData={notifyData}
        setNotifyData={setNotifyData}
        onSend={handleSendNotify}
        sending={sendingNotify}
      />
    </div>
  );
};

export default TransactionDetail;
